import type { ApiError } from '../../utils/api-client/api-error.ts'
import {
  MAX_REPOSITORIES_PER_REQUEST,
  ProjectFullProblemSchema,
  type Project,
} from '../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../utils/text-utils.ts'

/** The problem the membership endpoints raise when the destination cannot take the request. */
export const PROJECT_FULL_PROBLEM_TYPE = 'pixee:problems/api/project-memberships/project-full'

/**
 * How many more repositories a project can take, from the capacity and count it reports. Optimistic by
 * design — the client stops the obvious overshoot and the server refuses the rest.
 */
export const remainingCapacity = (project: Pick<Project, 'repository_capacity' | 'repository_count'>) =>
  Math.max(0, project.repository_capacity - project.repository_count)

/** Why "+ Add repositories" is unavailable on a full project. */
export const projectFullReason = (capacity: number) => `This project is full (${capacity} repositories).`

/**
 * Why the next row cannot be checked in the add dialog: the room left here, and the capacity it comes
 * from. The same arithmetic as {@link roomForMessage} — one helper, so the two cannot disagree.
 */
export const roomCapReason = ({ remaining, capacity }: { remaining: number; capacity: number }) =>
  `Up to ${remaining} more: a project holds at most ${capacity}.`

/**
 * How many repositories the add dialog lets the reader pick, and why it stops there: the room left in the
 * project, unless one request's limit binds first.
 */
export const addSelectionLimit = (project: Pick<Project, 'repository_capacity' | 'repository_count'>) => {
  const remaining = remainingCapacity(project)
  return remaining > MAX_REPOSITORIES_PER_REQUEST
    ? { cap: MAX_REPOSITORIES_PER_REQUEST, reason: `Up to ${MAX_REPOSITORIES_PER_REQUEST} at a time.` }
    : { cap: remaining, reason: roomCapReason({ remaining, capacity: project.repository_capacity }) }
}

/** Why a destination cannot take the whole selection, said in terms of what it can take. */
export const roomForMessage = (projectName: string, remaining: number) =>
  `${projectName} has room for ${remaining} more.`

export const isProjectFullError = (error: ApiError) =>
  error.status === 409 && error.problemType === PROJECT_FULL_PROBLEM_TYPE

/**
 * What to tell the reader when the server refused for want of room. The server's counts supersede the
 * client's estimate, since only the server knows them at the moment it refused: its own sentence when it
 * has one, otherwise the extension fields the contract guarantees.
 */
export const projectFullMessage = (error: ApiError): string => {
  if (error.problemDetail) {
    return error.problemDetail
  }
  const problem = ProjectFullProblemSchema.pick({ capacity: true, remaining: true }).safeParse(error.problemBody)
  if (!problem.success) {
    return 'The project is full.'
  }
  const { capacity, remaining } = problem.data
  return remaining === 0
    ? `The project is full (${capacity} repositories).`
    : `The project has room for ${remaining} more ${pluralize(remaining, 'repository', 'repositories')}.`
}
